import React, { useState } from 'react';
import { 
  FaStar, 
  FaMapMarkerAlt, 
  FaEnvelope, 
  FaPhone, 
  FaCalendarPlus, 
  FaUserGraduate,
  FaChalkboardTeacher,
  FaUsers,
  FaClock
} from 'react-icons/fa';
import { Link } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useToast } from '../../hooks/useToast';

/**
 * Component for displaying detailed mentor profile information
 */
const MentorProfile = ({ mentor }) => {
  const { isAuthenticated } = useAuth();
  const { showSuccess, showError } = useToast();
  
  const [activeTab, setActiveTab] = useState('about');
  const [showLoginPrompt, setShowLoginPrompt] = useState(false);
  
  // Render star ratings
  const renderRatingStars = (rating) => {
    const stars = [];
    for (let i = 1; i <= 5; i++) {
      stars.push(
        <FaStar key={i} className={i <= rating ? "text-yellow-500" : "text-gray-300"} />
      );
    }
    return <div className="flex space-x-1">{stars}</div>;
  };
  
  // Handle schedule session click
  const handleScheduleSession = () => {
    if (isAuthenticated) {
      // Navigate to session scheduling
      window.location.href = `/student/sessions/new?mentorId=${mentor._id}`;
    } else {
      setShowLoginPrompt(true);
    }
  };
  
  return (
    <div>
      {/* Mentor Header */}
      <div className="flex items-center mb-6">
        <div className="flex-shrink-0">
          {mentor.profileImage ? (
            <img
              src={mentor.profileImage}
              alt={`${mentor.firstName} ${mentor.lastName}`}
              className="h-24 w-24 rounded-full object-cover"
            />
          ) : (
            <div className="h-24 w-24 rounded-full bg-primary text-white flex items-center justify-center text-3xl font-semibold">
              {mentor.firstName.charAt(0)}
              {mentor.lastName.charAt(0)}
            </div>
          )}
        </div>
        
        <div className="ml-6">
          <h2 className="text-2xl font-semibold">
            {mentor.firstName} {mentor.lastName}
          </h2>
          <p className="text-gray-600 my-1">{mentor.expertise || 'Mentor'}</p>
          <div className="flex items-center mt-1">
            {renderRatingStars(mentor.rating || 0)}
            <span className="ml-2">({mentor.reviewCount || 0} reviews)</span>
          </div>
          
          {mentor.location && (
            <div className="text-gray-600 mt-2 flex items-center">
              <FaMapMarkerAlt className="mr-1" />
              {mentor.location.town ? `${mentor.location.town}, ` : ''}
              {mentor.location.county || 'Location unknown'}
            </div>
          )}
        </div>
      </div>
      
      {/* Navigation Tabs */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="flex space-x-8" aria-label="Mentor Profile">
          <button
            className={`py-4 px-1 text-sm font-medium border-b-2 ${
              activeTab === 'about' 
                ? 'border-primary text-primary' 
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
            onClick={() => setActiveTab('about')}
          >
            About
          </button>
          <button
            className={`py-4 px-1 text-sm font-medium border-b-2 ${
              activeTab === 'experience' 
                ? 'border-primary text-primary' 
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
            onClick={() => setActiveTab('experience')}
          >
            Experience & Education
          </button>
          <button
            className={`py-4 px-1 text-sm font-medium border-b-2 ${
              activeTab === 'reviews' 
                ? 'border-primary text-primary' 
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
            onClick={() => setActiveTab('reviews')}
          >
            Reviews
          </button>
          <button
            className={`py-4 px-1 text-sm font-medium border-b-2 ${
              activeTab === 'contact' 
                ? 'border-primary text-primary' 
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
            onClick={() => setActiveTab('contact')}
          >
            Contact
          </button>
        </nav>
      </div>
      
      {/* About Tab */}
      {activeTab === 'about' && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="md:col-span-2">
            <h3 className="text-lg font-semibold mb-4">About {mentor.firstName}</h3>
            <p className="text-gray-700 mb-6">
              {mentor.bio || "No bio available."}
            </p>
            
            <h4 className="font-semibold mb-3">Specialties</h4>
            <div className="flex flex-wrap gap-2 mb-6">
              {mentor.specialties && mentor.specialties.length > 0 ? (
                mentor.specialties.map((specialty, idx) => (
                  <span 
                    key={idx} 
                    className="px-3 py-1 bg-blue-100 text-blue-800 text-sm rounded-full"
                  >
                    {specialty}
                  </span>
                ))
              ) : (
                <p className="text-gray-500">No specialties listed</p>
              )}
            </div>
            
            <h4 className="font-semibold mb-3">Availability</h4>
            <div className="mb-6">
              {mentor.availability && mentor.availability.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {mentor.availability.includes('weekdays') && (
                    <span className="flex items-center px-3 py-1 bg-green-100 text-green-800 text-sm rounded-full">
                      <FaClock className="mr-1" /> Weekdays
                    </span>
                  )}
                  {mentor.availability.includes('weekends') && (
                    <span className="flex items-center px-3 py-1 bg-purple-100 text-purple-800 text-sm rounded-full">
                      <FaClock className="mr-1" /> Weekends
                    </span>
                  )}
                  {mentor.availability.includes('evenings') && (
                    <span className="flex items-center px-3 py-1 bg-yellow-100 text-yellow-800 text-sm rounded-full">
                      <FaClock className="mr-1" /> Evenings
                    </span>
                  )}
                </div>
              ) : (
                <p className="text-gray-500">No availability information</p>
              )}
            </div>
          </div>
          
          <div>
            <div className="bg-gray-50 p-4 rounded-lg mb-4">
              <h4 className="font-semibold mb-3">Session Statistics</h4>
              {mentor.sessions ? (
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-700 flex items-center">
                      <FaChalkboardTeacher className="mr-2 text-primary" /> Sessions Completed
                    </span>
                    <span className="font-semibold">{mentor.sessions.completed || 0}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-700 flex items-center">
                      <FaClock className="mr-2 text-primary" /> Teaching Hours
                    </span>
                    <span className="font-semibold">{mentor.sessions.hours || 0}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-700 flex items-center">
                      <FaUsers className="mr-2 text-primary" /> Students Mentored
                    </span>
                    <span className="font-semibold">{mentor.sessions.students || 0}</span>
                  </div>
                </div>
              ) : (
                <p className="text-gray-500">No session statistics available</p>
              )}
            </div>
            
            <div className="mt-4">
              <button
                onClick={handleScheduleSession}
                className="w-full py-2 bg-primary text-white rounded-lg hover:bg-primary-dark flex items-center justify-center"
              >
                <FaCalendarPlus className="mr-2" /> Schedule a Session
              </button>
              
              {!isAuthenticated && (
                <p className="text-xs text-center mt-2 text-gray-500">
                  <Link to="/login" className="text-primary">Login</Link> or <Link to="/register" className="text-primary">register</Link> to schedule
                </p>
              )}
            </div>
          </div>
        </div>
      )}
      
      {/* Experience & Education Tab */}
      {activeTab === 'experience' && (
        <div>
          <h3 className="text-lg font-semibold mb-4">Experience & Education</h3>
          
          <div className="mb-6">
            <h4 className="font-semibold text-gray-700 mb-3 flex items-center">
              <FaChalkboardTeacher className="mr-2 text-primary" /> Teaching Experience
            </h4>
            <div className="pl-8 border-l-2 border-gray-200">
              <div className="mb-4 relative">
                <div className="absolute -left-10 top-0 w-4 h-4 rounded-full bg-primary"></div>
                <div className="font-medium">Professional Educator</div>
                <div className="text-sm text-gray-600">
                  {mentor.experience ? `${mentor.experience} years` : 'Experience details not available'}
                </div>
                <p className="mt-1 text-gray-700">
                  {mentor.bio ? mentor.bio.split('.')[0] + '.' : 'No experience details available.'}
                </p>
              </div>
            </div>
          </div>
          
          <div>
            <h4 className="font-semibold text-gray-700 mb-3 flex items-center">
              <FaUserGraduate className="mr-2 text-primary" /> Education
            </h4>
            {mentor.education && mentor.education.length > 0 ? (
              <div className="pl-8 border-l-2 border-gray-200">
                {mentor.education.map((edu, index) => (
                  <div key={index} className="mb-4 relative">
                    <div className="absolute -left-10 top-0 w-4 h-4 rounded-full bg-primary"></div>
                    <div className="font-medium">{edu.degree}</div>
                    <div className="text-sm text-gray-600">{edu.institution} • {edu.year}</div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500">No education details available</p>
            )}
          </div>
        </div>
      )}
      
      {/* Reviews Tab */}
      {activeTab === 'reviews' && (
        <div>
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold">Reviews</h3>
            <div className="flex items-center">
              {renderRatingStars(mentor.rating || 0)}
              <span className="ml-2 font-medium">{mentor.rating || 0}/5</span>
              <span className="ml-1 text-sm text-gray-500">({mentor.reviewCount || 0})</span>
            </div>
          </div>
          
          {/* Example Reviews */}
          <div className="space-y-6">
            {mentor.reviewCount && mentor.reviewCount > 0 ? (
              [
                {
                  id: 1,
                  name: 'Jane Doe',
                  date: '2 months ago',
                  rating: 5,
                  comment: 'Excellent mentor who really cares about student success. My child improved significantly in mathematics after just a few sessions.'
                },
                {
                  id: 2,
                  name: 'Michael Smith',
                  date: '4 months ago',
                  rating: 4,
                  comment: 'Very knowledgeable and patient. Explains complex concepts in an easy-to-understand way.'
                }
              ].map(review => (
                <div key={review.id} className="border-b border-gray-200 pb-6">
                  <div className="flex justify-between items-start">
                    <div className="font-medium">{review.name}</div>
                    <div className="text-sm text-gray-500">{review.date}</div>
                  </div>
                  <div className="mt-1">
                    {renderRatingStars(review.rating)}
                  </div>
                  <p className="mt-2 text-gray-700">
                    {review.comment}
                  </p>
                </div>
              ))
            ) : (
              <div className="text-center py-8">
                <div className="text-gray-400 mb-3">
                  <FaStar className="inline-block text-3xl" />
                </div>
                <p className="text-gray-500">No reviews yet</p>
                {isAuthenticated ? (
                  <button className="mt-2 text-primary font-medium">
                    Be the first to leave a review
                  </button>
                ) : (
                  <p className="mt-2 text-sm text-gray-500">
                    <Link to="/login" className="text-primary">Login</Link> to leave a review
                  </p>
                )}
              </div>
            )}
          </div>
          
          {mentor.reviewCount > 2 && (
            <div className="mt-4 text-center">
              <button className="text-primary font-medium">
                Show all {mentor.reviewCount} reviews
              </button>
            </div>
          )}
        </div>
      )}
      
      {/* Contact Tab */}
      {activeTab === 'contact' && (
        <div>
          <h3 className="text-lg font-semibold mb-4">Contact Information</h3>
          
          {isAuthenticated ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h4 className="font-medium text-gray-700 mb-3">Contact Details</h4>
                {mentor.contactEmail && (
                  <p className="flex items-center mb-3">
                    <FaEnvelope className="mr-2 text-gray-500" />
                    <a href={`mailto:${mentor.contactEmail}`} className="text-primary hover:underline">
                      {mentor.contactEmail}
                    </a>
                  </p>
                )}
                {mentor.contactPhone && (
                  <p className="flex items-center mb-3">
                    <FaPhone className="mr-2 text-gray-500" />
                    <a href={`tel:${mentor.contactPhone}`} className="text-primary hover:underline">
                      {mentor.contactPhone}
                    </a>
                  </p>
                )}
                {mentor.location && (
                  <p className="flex items-center mb-3">
                    <FaMapMarkerAlt className="mr-2 text-gray-500" />
                    {mentor.location.town ? `${mentor.location.town}, ` : ''}
                    {mentor.location.county || 'Location unknown'}
                  </p>
                )}
              </div>
              
              <div>
                <h4 className="font-medium text-gray-700 mb-3">Send Message</h4>
                <div className="space-y-3">
                  <textarea 
                    className="form-textarea w-full" 
                    rows="5" 
                    placeholder="Type your message here..."
                  ></textarea>
                  <button className="w-full py-2 bg-primary text-white rounded hover:bg-primary-dark">
                    Send Message
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <div className="bg-blue-50 p-6 rounded-lg text-center">
              <FaUserGraduate className="mx-auto text-blue-500 text-3xl mb-3" />
              <h4 className="font-semibold text-xl mb-2">Contact details are private</h4>
              <p className="text-gray-700 mb-4">
                You need to be logged in to view contact information and send messages to mentors.
              </p>
              <div className="flex justify-center space-x-4">
                <Link 
                  to="/login" 
                  className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark"
                >
                  Login
                </Link>
                <Link 
                  to="/register" 
                  className="px-4 py-2 bg-gray-100 text-gray-800 rounded-lg hover:bg-gray-200"
                >
                  Register
                </Link>
              </div>
            </div>
          )}
        </div>
      )}
      
      {/* Login Prompt Modal */}
      {showLoginPrompt && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg max-w-md w-full">
            <h3 className="text-lg font-semibold mb-4">Login Required</h3>
            <p className="mb-4">Please log in or register to schedule a session with this mentor.</p>
            <div className="flex justify-end space-x-3">
              <button 
                className="px-4 py-2 border border-gray-300 rounded"
                onClick={() => setShowLoginPrompt(false)}
              >
                Cancel
              </button>
              <Link 
                to="/login" 
                className="px-4 py-2 bg-primary text-white rounded"
              >
                Login
              </Link>
              <Link 
                to="/register" 
                className="px-4 py-2 bg-secondary text-white rounded"
              >
                Register
              </Link>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MentorProfile; 